import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Title } from 'react-admin';
import { getRoles, isAdmin, isVendor } from '../../core/roles.ts';
import { rememberShabbatOnly } from '../../core/shabbatOnly.ts';
import ShabbatService, {
    type MealType, type MealStatus, type RegistrationMealLine, type ShabbatVenue, type VendorMeal, type VendorRegistration,
} from '../../services/shabbat/shabbatService.ts';

export const shabbatService = new ShabbatService();

// ------------------------------------------------------------------ time
// Every time on these pages is the VENUE's time, never the browser's: a host
// in Larnaca and an admin in Tel Aviv must read the same "18:30". Local dates
// and times come from the server as plain strings; instants carry an offset
// and are shown in the venue's zone.

/** "Fri 16 Oct" for a local "2026-10-16". */
export const fmtDate = (date: string | null | undefined): string => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
        .format(new Date(date + 'T12:00:00Z'));
};

/** "Fri 16 Oct · 18:30" from a local date and time. */
export const fmtDateTime = (date: string | null | undefined, time: string | null | undefined): string =>
    date ? `${fmtDate(date)} · ${time ?? ''}`.trim() : '';

const parts = (iso: string, timeZone: string) => {
    const p: Record<string, string> = {};
    new Intl.DateTimeFormat('en-GB', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(iso)).forEach(x => { p[x.type] = x.value; });
    return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

/** "Thu 15 Oct · 18:00" for an instant, in the venue's zone. */
export const fmtInstant = (iso: string | null | undefined, timeZone: string): string => {
    if (!iso) return '';
    const { date, time } = parts(iso, timeZone);
    return fmtDateTime(date, time);
};

/** Now in the venue's zone, as "2026-10-15T18:00", comparable with local date + time strings. */
export const nowLocal = (timeZone: string): string => {
    const { date, time } = parts(new Date().toISOString(), timeZone);
    return `${date}T${time}`;
};

/** 0 = Sunday ... 6 = Saturday, for a local date string. */
export const weekdayOf = (date: string): number => new Date(date + 'T12:00:00Z').getUTCDay();

export const addDays = (date: string, days: number): string => {
    const d = new Date(date + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

export const mealName = (type: MealType): string =>
    type === 'FRIDAY_DINNER' ? 'Friday night dinner' : 'Shabbat lunch';

// With the time: a venue can hold two Friday dinners (an early and a late sitting),
// and the day alone made their tabs identical.
export const mealTab = (meal: VendorMeal): string => `${mealName(meal.type)} · ${fmtDate(meal.date)} · ${meal.startTime}`;

export const STATUS_CHIP: Record<MealStatus, { label: string; color: string; bg: string }> = {
    OPEN: { label: 'Open', color: '#03676A', bg: '#DDF4F1' },
    FULL: { label: 'Full', color: '#B91C1C', bg: '#FEE2E2' },
    CLOSED: { label: 'Closed', color: '#6B7280', bg: '#F3F4F6' },
};

export const StatusChip: React.FC<{ label: string; color: string; bg: string }> = ({ label, color, bg }) => (
    <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color, bgcolor: bg, borderRadius: '10px', px: 1.25, py: 0.4, whiteSpace: 'nowrap' }}>
        {label}
    </Box>
);

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const party = (adults: number, children: number) =>
    plural(adults, 'adult', 'adults') + (children ? ' · ' + plural(children, 'child', 'children') : '');

/** The registration's other meals (not this one), when it covers more than one. */
export const otherMeals = (r: VendorRegistration, mealId: number): RegistrationMealLine[] =>
    (r.meals ?? []).filter(line => line.mealId !== mealId);

/** Still covers two or more meals (the blue "Dinner + lunch" chip): the server's flag, else the active meal lines. */
export const isCombined = (r: VendorRegistration): boolean =>
    r.combined ?? (r.meals ?? []).filter(line => line.status === 'ACTIVE').length > 1;

/** Was booked for more than one meal (its other meals are listed, cancelled ones too). */
export const hasOtherMeals = (r: VendorRegistration): boolean => (r.meals?.length ?? 0) > 1;

/** "Also Shabbat lunch: 2 adults · 1 child", or "Shabbat lunch cancelled". */
export const otherMealText = (line: RegistrationMealLine): string =>
    line.status === 'CANCELLED'
        ? `${mealName(line.type)} cancelled`
        : `Also ${mealName(line.type)}: ${party(line.adults, line.children)}`;

/** "Dinner + lunch" chip (design v3: blue) for a registration covering both meals. */
export { DinnerLunchChip as CombinedBadge } from '../../components/customer/CustomerChips.tsx';

// ------------------------------------------------------------------ venues
// Which venues this login runs Shabbat meals for. Asked once per page load
// and shared by the menu and the three pages: a restaurant login gets its own
// venue when an admin has switched it on, an admin every hosting venue. A
// failed call reads as "none", which hides the pages rather than breaking
// the menu.

let venuesPromise: Promise<ShabbatVenue[]> | null = null;

/** Signed in as far as the panel knows: what the login writes and signing out removes. */
const isSignedIn = (): boolean => {
    try {
        return localStorage.getItem('auth') === 'true' && getRoles().length > 0;
    } catch {
        return false;
    }
};
const listeners = new Set<(venues: ShabbatVenue[]) => void>();

/**
 * The venues, asked once and shared. `fresh` asks again and tells every page
 * and the menu - used after an admin switches a venue's Shabbat setting.
 */
export const loadShabbatVenues = (fresh = false): Promise<ShabbatVenue[]> => {
    // Only with a session. The menu mounts for a moment before react-admin's
    // auth check sends a signed-out visitor to the login page; asking then got
    // a 401, the axios interceptor signed out, and /login reloaded itself
    // about three times a second (staging, 15 Oct 2026). Not cached, so the
    // menu asks again once someone has signed in.
    if (!isSignedIn()) {
        return Promise.resolve([]);
    }
    if (!venuesPromise || fresh) {
        const p = shabbatService.venues().then(v => {
            // A Chabad House login gets the Shabbat-only panel. A session from
            // before that existed, or a venue an admin has just switched,
            // reloads once into the right one.
            if (rememberShabbatOnly(v) && isVendor()) window.location.reload();
            return v;
        }).catch(() => {
            venuesPromise = null;
            return [] as ShabbatVenue[];
        });
        venuesPromise = p;
        if (fresh) p.then(v => listeners.forEach(l => l(v)));
    }
    return venuesPromise;
};

export const useShabbatVenues = () => {
    const [venues, setVenues] = useState<ShabbatVenue[] | null>(null);
    useEffect(() => {
        let alive = true;
        const listener = (v: ShabbatVenue[]) => { if (alive) setVenues(v); };
        listeners.add(listener);
        loadShabbatVenues().then(listener);
        return () => { alive = false; listeners.delete(listener); };
    }, []);
    return venues;
};

const VENUE_KEY = 'vello.shabbat.venue';

const storedVenue = (): number | null => {
    try {
        const v = Number(localStorage.getItem(VENUE_KEY));
        return Number.isFinite(v) && v > 0 ? v : null;
    } catch {
        return null;
    }
};

/**
 * The venue the pages show; null means every venue together.
 *
 * A restaurant login has exactly one and never gets "all". An admin on a page
 * that offers "All venues" (Shabbat meals, Registrations) starts on all of
 * them (Ariel, 23 Oct 2026); elsewhere (the guest list, which prints one
 * venue) on the last venue picked in this browser. Either way a link names
 * its venue with ?venue=, or with ?meal= whose venue is looked up, so
 * "Registrations" on a meal card opens that meal's venue rather than the
 * first one.
 */
export const useSelectedVenue = (venues: ShabbatVenue[] | null, allowAll: boolean) => {
    const admin = isAdmin();
    const [params, setParams] = useSearchParams();
    const fromUrl = Number(params.get('venue')) || null;
    const mealId = Number(params.get('meal')) || null;
    const [mealVenue, setMealVenue] = useState<number | null>(null);
    const [resolving, setResolving] = useState<boolean>(admin && !fromUrl && !!mealId);

    useEffect(() => {
        if (!admin || fromUrl || !mealId) { setResolving(false); return; }
        let alive = true;
        setResolving(true);
        shabbatService.registrations(mealId)
            .then(r => { if (alive) setMealVenue(r.meal?.locationId ?? null); })
            .catch(() => { /* an unknown meal: fall back to the default */ })
            .finally(() => { if (alive) setResolving(false); });
        return () => { alive = false; };
    }, [admin, fromUrl, mealId]);

    const find = (id: number | null) => (id ? venues?.find(v => v.id === id) ?? null : null);
    let venue: ShabbatVenue | null;
    if (!admin) {
        venue = venues?.[0] ?? null;
    } else {
        venue = find(fromUrl) ?? find(mealVenue) ?? (allowAll ? null : find(storedVenue()) ?? venues?.[0] ?? null);
    }
    const all = admin && allowAll && venue === null;

    const choose = (next: number | null) => {
        const p = new URLSearchParams(params);
        p.delete('meal');
        if (next) {
            p.set('venue', String(next));
            try { localStorage.setItem(VENUE_KEY, String(next)); } catch { /* per-browser convenience only */ }
        } else {
            p.delete('venue');
        }
        setParams(p, { replace: true });
    };
    return { venue, all, choose, resolving } as const;
};

/** "Name · City · Shabbat only (not hosting now)" for the admin's venue lists. */
export const venueLabel = (v: ShabbatVenue): string =>
    `${v.name}${v.cityName ? ` · ${v.cityName}` : ''}${v.shabbatOnly ? ' · Shabbat only' : ''}${v.hostsShabbat ? '' : ' (not hosting now)'}`;

/** The venues a new meal can go to: those hosting now. */
export const hostingVenues = (venues: ShabbatVenue[]): ShabbatVenue[] => venues.filter(v => v.hostsShabbat);

// ------------------------------------------------------------------ earlier Shabbats
// Admins can look back (25 Oct 2026): the lists start at this Shabbat unless
// ?since= asks for earlier ones, so a venue switched off keeps its history
// reachable. Restaurant logins never get the picker (unchanged).

const SINCE: { key: string; label: string; days: number }[] = [
    { key: '', label: 'From this Shabbat', days: 0 },
    { key: '4w', label: 'Last 4 weeks', days: 28 },
    { key: '3m', label: 'Last 3 months', days: 92 },
    { key: '12m', label: 'Last 12 months', days: 366 },
];

/** The "from" date (YYYY-MM-DD) the lists ask for, or null for this Shabbat on. Admins only. */
export const useSince = () => {
    const [params, setParams] = useSearchParams();
    const key = isAdmin() ? params.get('since') ?? '' : '';
    const option = SINCE.find(o => o.key === key) ?? SINCE[0];
    const from = option.days ? addDays(new Date().toISOString().slice(0, 10), -option.days) : null;
    const choose = (next: string) => {
        const p = new URLSearchParams(params);
        p.delete('meal');
        p.delete('slot');
        if (next) p.set('since', next); else p.delete('since');
        setParams(p, { replace: true });
    };
    return { key: option.key, from, choose } as const;
};

/** The admin's "From this Shabbat / Last 4 weeks / ..." picker; nothing for a restaurant login. */
export const SincePicker: React.FC = () => {
    const { key, choose } = useSince();
    if (!isAdmin()) return null;
    return (
        <TextField select size="small" label="Show" value={key} onChange={e => choose(e.target.value)} sx={{ minWidth: 180, bgcolor: '#fff' }}>
            {SINCE.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
        </TextField>
    );
};

/** For an admin on a venue whose switch is now off: what it can still do there. */
export const NotHostingNote: React.FC<{ venue: ShabbatVenue | null }> = ({ venue }) =>
    venue && !venue.hostsShabbat ? (
        <Alert severity="warning" sx={{ maxWidth: 760, mb: 2 }}>
            {venue.name} does not host Shabbat meals now. Its meals and registrations stay readable (earlier Shabbats:
            Registrations, Show "Last 3 months"); new meals and edits need "Hosts Shabbat meals" switched back on.
        </Alert>
    ) : null;

// ------------------------------------------------------------------ frame

type FrameProps = {
    title: string;
    subtitle: string;
    /** Offer admins "All venues" (first, and the default). */
    allowAll?: boolean;
    /** venue null = all venues (admins on an allowAll page only). */
    actions?: (venue: ShabbatVenue | null, venues: ShabbatVenue[]) => React.ReactNode;
    children: (venue: ShabbatVenue | null, venues: ShabbatVenue[]) => React.ReactNode;
};

/**
 * The header every Shabbat page shares: title and subtitle, the venue (a
 * picker for admins, "All venues" first where the page offers it), and the
 * page's own buttons. Hidden when printing.
 */
export const ShabbatFrame: React.FC<FrameProps> = ({ title, subtitle, allowAll = false, actions, children }) => {
    const venues = useShabbatVenues();
    const { venue, all, choose, resolving } = useSelectedVenue(venues, allowAll);
    const admin = isAdmin();
    const ready = venues !== null && !resolving;
    const showing = ready && (venue || (all && venues.length > 0));

    return (
        // contain: wide tables scroll inside their card instead of widening the page past the sidebar.
        <Box data-shabbat-frame="1" sx={{ pb: 6, bgcolor: '#F3F5F4', minHeight: 'calc(100vh - 72px)', width: '100%', minWidth: 0, contain: 'inline-size' }}>
            <Title title={title} />
            <Box
                data-noprint="1"
                sx={{
                    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                    px: { xs: 2, md: 3.5 }, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #E5E7EB',
                }}
            >
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{title}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                        {venue ? `${venue.name} · ${subtitle}` : all && showing ? `All venues · ${subtitle}` : subtitle}
                    </Typography>
                </Box>
                {admin && ready && venues.length > 0 && (venue || all) && (
                    <TextField
                        select
                        size="small"
                        label="Venue"
                        value={venue?.id ?? 0}
                        onChange={e => choose(Number(e.target.value) || null)}
                        sx={{ minWidth: 240, bgcolor: '#fff' }}
                    >
                        {allowAll && <MenuItem value={0}>All venues</MenuItem>}
                        {venues.map(v => (
                            <MenuItem key={v.id} value={v.id}>
                                {venueLabel(v)}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {showing && actions && actions(venue, venues ?? [])}
            </Box>
            <Box data-print-root="1" sx={{ px: { xs: 2, md: 3.5 }, pt: 3 }}>
                {!ready && <CircularProgress size={24} />}
                {ready && !showing && (
                    <Alert severity="info" sx={{ maxWidth: 640 }}>
                        {admin
                            ? 'No venue hosts Shabbat meals yet. Switch on "Hosts Shabbat meals" on a restaurant\'s settings page.'
                            : 'Shabbat meals are not switched on for this venue. Ask Vello to turn them on.'}
                    </Alert>
                )}
                {showing && children(venue, venues ?? [])}
            </Box>
        </Box>
    );
};

/** Meal tabs shared by Registrations and the guest list. */
export const MealTabs: React.FC<{ meals: VendorMeal[]; selected: number | null; onPick: (id: number) => void }> = ({ meals, selected, onPick }) => (
    <Box data-noprint="1" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {meals.map(m => {
            const on = m.id === selected;
            return (
                <Box
                    key={m.id}
                    component="button"
                    type="button"
                    onClick={() => onPick(m.id)}
                    sx={{
                        height: 36, borderRadius: '18px', px: 2, fontSize: 14, cursor: on ? 'default' : 'pointer',
                        fontFamily: 'inherit', fontWeight: on ? 700 : 400,
                        bgcolor: on ? '#24BBAA' : '#fff', color: on ? '#fff' : '#374151',
                        border: on ? '1px solid #24BBAA' : '1px solid #E5E7EB',
                        '&:hover': on ? {} : { borderColor: '#24BBAA' },
                    }}
                >
                    {mealTab(m)}
                </Box>
            );
        })}
    </Box>
);

/** The meal from ?meal= when it is one of these, else the first that has not happened yet, else the first. */
export const pickMeal = (meals: VendorMeal[], requested: number | null, timeZone: string): number | null => {
    if (requested && meals.some(m => m.id === requested)) return requested;
    const now = nowLocal(timeZone);
    const upcoming = meals.find(m => `${m.date}T${m.startTime}` >= now);
    return (upcoming ?? meals[0])?.id ?? null;
};
